/**
 * Round-robin emulation: the small differences between two strikes of the
 * same drum, or two plays of the same key, that a single sample lacks.
 * Without them repeated hits sound mechanical (the "machine gun").
 *
 * Variation is deterministic, derived from where a hit sits (clip, lane,
 * beat) or from the note's play count, never from Math.random(): live
 * playback, offline export and re-renders of the same music are identical.
 * The amounts stay well below what reads as out of tune or uneven.
 */
export const HUMANIZE_DEFAULT = 0.5;

// Range at amount 1; the default (0.5) is half of these.
const DRUM_PITCH_CENTS = 8;
const DRUM_LEVEL_DB = 1.2;
const NOTE_PITCH_CENTS = 5;
const NOTE_LEVEL_DB = 1;
const NOTE_TONE_OCTAVES = 0.15;

const NONE = Object.freeze({ rateFactor: 1, gainFactor: 1, toneFactor: 1, cents: 0 });

/** FNV-1a: a stable 32-bit hash of a string, scaled to [0, 1). */
export function hashUnit(text) {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967296;
}

// Two uniform draws summed: a triangular spread in (-1, 1), mostly near 0,
// so most hits differ a little and few differ by the full range.
const spread = (key) => hashUnit(`${key}:a`) + hashUnit(`${key}:b`) - 1;
const amountOf = (amount) => Math.min(1, Math.max(0, Number.isFinite(Number(amount)) ? Number(amount) : HUMANIZE_DEFAULT));

/**
 * Per-hit variation for a drum-lane hit at an absolute timeline beat.
 * Pitched hits (808 notes, one-shot instruments) keep exact pitch and only
 * vary in level.
 */
export function drumHitVariation({ clipId, drumIndex, beat, pitched = false, amount = HUMANIZE_DEFAULT }) {
  const depth = amountOf(amount);
  if (depth === 0) return NONE;
  const key = `${clipId}|${drumIndex}|${Math.round((Number(beat) || 0) * 960)}`;
  const cents = pitched ? 0 : spread(`${key}|pitch`) * DRUM_PITCH_CENTS * depth;
  const db = spread(`${key}|level`) * DRUM_LEVEL_DB * depth;
  return { rateFactor: 2 ** (cents / 1200), gainFactor: 10 ** (db / 20), toneFactor: 1, cents };
}

/**
 * Per-note variation for a sampled-instrument note: the play count of that
 * note on that layer decides it, so a repeated key never sounds identical twice.
 */
export function noteVariation({ layerId, note, playCount, amount = HUMANIZE_DEFAULT }) {
  const depth = amountOf(amount);
  if (depth === 0) return NONE;
  const key = `${layerId}|${note}|${playCount}`;
  const cents = spread(`${key}|pitch`) * NOTE_PITCH_CENTS * depth;
  const db = spread(`${key}|level`) * NOTE_LEVEL_DB * depth;
  const toneFactor = 2 ** (spread(`${key}|tone`) * NOTE_TONE_OCTAVES * depth);
  return { rateFactor: 2 ** (cents / 1200), gainFactor: 10 ** (db / 20), toneFactor, cents };
}
